import {
	useEffect,
	useState,
	ReactElement,
	useCallback,
	useMemo
} from "react";
import {
	Dialog,
	FormattedText,
	getPropertySortText,
	OptionsMenu,
	OptionsMenuItem,
	ReorderableList,
	SearchInputForm,
	Toolbar,
	TransparentButtonList,
	Typography
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	getVisibilitySettings,
	saveViewsVisibility,
	SharePointSiteCollectionOrderKind,
	ViewsVisibilityModel,
	ViewVisibilityNodeModel
} from "client/Api/Documents/SharePoint/documentViewStructureCustomization";
import {
	AlertManager,
	useAlerts,
	useStableImperativeCallback,
	useUnmountedCancellation
} from "client/Hooks";
import { reloadCurrentPage } from "client_legacy/ndaw/navigator";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import styles from "./VisibilityConfiguration.module.less";
import { DataSourceSortDirection, nativeSortFunction } from "client/Data/sorting";
import { parseDtoDate } from "client/DateAndTime/dateTimeUtils";
import {
	setVisibilityRecursive,
	VisibilityNodeData,
	visibleUnderSearchTerm
} from "./common";
import { VisibilityRootNode } from "./VisibilityRootNode";
import { NormalizedSearchTerm, normalizeForSearch } from "client/Data/searching";

export type VisibilityConfigurationProps = {
	onClose(): void;
};

export const VisibilityConfiguration = (props: VisibilityConfigurationProps): ReactElement => {
	const {
		onClose: onCloseFromProps
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<string>("");

	const [formData, setFormData] = useState<FormData>({
		rootNodes: [],
		orderKind: SharePointSiteCollectionOrderKind.Custom
	});

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const onClose: () => void = useStableImperativeCallback(onCloseFromProps);

	useEffect(
		(): (() => void) => {

			const load = async (signal: AbortSignal): Promise<void> => {
				setIsBusy(true);

				try {
					const model: ViewsVisibilityModel = await getVisibilitySettings(signal);
					setFormData({
						rootNodes: model.nodes
							.map(convertModelToNode),
						orderKind: model.orderKind
					});

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose();
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[alertManager, onClose]
	);

	const showAll = () => setAllVisible(true);
	const hideAll = () => setAllVisible(false);

	const setAllVisible = (isVisible: boolean) => {
		setFormData(
			(x): FormData => {
				return {
					...x,
					rootNodes: x.rootNodes
						.map(n => setVisibilityRecursive(n, isVisible, ""))
				};
			}
		);
	};

	const changeOrderKindHandler = useCallback(
		(orderKind: SharePointSiteCollectionOrderKind): void => {
			setFormData(
				(x): FormData => {
					return {
						...x,
						orderKind,
						rootNodes: applySorting(x.rootNodes, orderKind)
					};
				}
			);
		},
		[]
	);

	const itemReorderHandler = useCallback(
		(): void => {
			const from: SharePointSiteCollectionOrderKind = formData.orderKind;
			const to: SharePointSiteCollectionOrderKind = SharePointSiteCollectionOrderKind.Custom;

			if (to === from) {
				return;
			}

			changeOrderKindHandler(to);

			alertManager.showInfo(
				<FormattedText
					text={localize("_SortingChangedFromNameToName")}
					thingsToReplace={[
						{ what: "{{From}}", filler: <b>{orderToNameMap[from]}</b> },
						{ what: "{{To}}", filler: <b>{orderToNameMap[to]}</b> }
					]}
				/>
			);
		},
		[
			alertManager,
			changeOrderKindHandler,
			formData.orderKind
		]
	);

	const itemMoveHandler = useCallback(
		(fromIndex: number, toIndex: number): void => {
			itemReorderHandler();

			setFormData(
				(x): FormData => {
					const newRootNodes: VisibilityNodeData[] = [...x.rootNodes];
					const rootNode: VisibilityNodeData = newRootNodes.splice(fromIndex, 1)[0];
					newRootNodes.splice(toIndex, 0, rootNode);

					return {
						...x,
						rootNodes: newRootNodes
					};
				}
			);
		},
		[itemReorderHandler]
	);

	const onRootNodeChange = useCallback(
		(changedRootNode: VisibilityNodeData): void => {
			setFormData(
				(formData: FormData): FormData => {
					const nodeIndex: number = formData.rootNodes
						.findIndex((x) => x.id === changedRootNode.id);

					if (nodeIndex === -1) {
						throw new Error(`Node was not identified as root node '${JSON.stringify(changedRootNode)}'`);
					}

					const newRootNodes: VisibilityNodeData[] = [...formData.rootNodes];
					newRootNodes.splice(nodeIndex, 1, changedRootNode);

					return {
						...formData,
						rootNodes: newRootNodes
					};
				}
			);
		},
		[]
	);

	const saveHandler = async (): Promise<void> => {
		const { rootNodes, orderKind } = formData;
		const hiddenLibraryIds: string[] = combineIdsInArray(rootNodes.map((x) => summarizeHiddenLibraries(x)));
		const rootSiteIdOrder: string[] = rootNodes.map((x) => x.id);
		const hasAtLeastOneSiteCollectionVisible: boolean = rootNodes.some((x) => x.isVisible);

		if (!hasAtLeastOneSiteCollectionVisible) {
			alertManager.showError(localize("_AtLeastOneLibraryShouldBeActivated"));
			return;
		}

		setIsBusy(true);

		try {
			await saveViewsVisibility(
				{
					hiddenLibrariesIds: hiddenLibraryIds,
					rootSiteIdOrder,
					orderKind
				},
				globalCancellation
			);

			alertManager.showSuccess(localize("_ChangesSaved"));
			// Wait a bit for the alert to be displayed before reloading the page
			setTimeout(() => reloadCurrentPage(), 1000);
		} catch (e) {
			if (isCancelledError(e)) {
				return;
			}
			console.error(e);
			setIsBusy(false);
			alertManager.showError(localize("_GeneralErrorMessage"));
		}
	};

	const getOverlayTextHandler = useCallback(
		(activeIndex: number): string => {
			return formData.rootNodes[activeIndex]
				.name;
		},
		[formData.rootNodes]
	);

	const normalizedSearchTerm = useMemo(
		(): NormalizedSearchTerm => normalizeForSearch(searchTerm),
		[searchTerm]
	);

	const nodesToDisplay = useMemo(
		(): VisibilityNodeData[] => {
			return formData.rootNodes
				.filter(x => visibleUnderSearchTerm(x, normalizedSearchTerm));
		},
		[formData.rootNodes, normalizedSearchTerm]
	);

	const isMovingAllowed: boolean = !searchTerm;

	return (
		<Dialog
			hasBusyIndicator={isBusy}
			onClose={onClose}
			variant="left-slider"
			trackingId="manage-site-visibility-sidebar"
		>
			<Dialog.Header
				title={localize("_ManageLibraries")}
				onClose={onClose}
				hasBorder
			/>
			<Dialog.Body>
				<p>
					{localize("_HereYouCanDetermineTheOrderAndVisibilityOfTheDocumentLibraries")}
				</p>
				<div className={styles.searchAndSort}>
					<SearchInputForm
						onChange={(x) => setSearchTerm(x.trim())}
						hasMarginBottom
						trackingId="search-term"
						placeholder={(
							<FormattedText
								text={localize("_FilterByProperties")}
								thingsToReplace={[
									{
										what: "{{Properties}}",
										filler: (
											<Typography weight="bold" isItalic>
												{localize("_Libraries")}
											</Typography>
										)
									}
								]}
							/>
						)}
						height="large"
						icon="filter"
					/>
					<OptionsMenu
						value={formData.orderKind}
						onChange={changeOrderKindHandler}
						title={localize("_SortBy")}
						options={orderOptions}
						trackingId="sort-by"
						buttonSize="large"
						icon="arrow-up-arrow-down"
					/>
				</div>
				<div className={styles.transparentButtonListContainer}>
					<TransparentButtonList
					>
						<TransparentButtonList.Item
							icon="eye"
							text={localize("_ShowAllLibraries")}
							onClick={showAll}
							trackingId="show-all"
						/>
						<TransparentButtonList.Item
							icon="eye-slash"
							text={localize("_HideAllLibraries")}
							onClick={hideAll}
							trackingId="hide-all"
						/>
					</TransparentButtonList>
				</div>
				<ReorderableList
					onItemMove={isMovingAllowed ? itemMoveHandler : undefined}
					trackingId="visibility-nodes-reorderable-list"
					hasSeparator
					getOverlayText={getOverlayTextHandler}
				>
					{nodesToDisplay.map((x) => (
						<VisibilityRootNode
							key={x.id}
							data={x}
							onChange={onRootNodeChange}
							searchTerm={normalizedSearchTerm}
						/>
					))}
				</ReorderableList>
			</Dialog.Body>
			<Dialog.Footer hasBorder>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Save")}
						onClick={saveHandler}
						trackingId="save"
					/>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};

VisibilityConfiguration.displayName = "VisibilityConfiguration";

function convertModelToNode(model: ViewVisibilityNodeModel): VisibilityNodeData {
	return {
		id: model.id,
		name: model.name,
		createdOn: parseDtoDate(model.createdOn),
		isVisible: model.isVisible,
		children: model.children?.map(convertModelToNode) ?? [],
		isCollapsed: true
	};
};

function combineIdsInArray(nodeIds: ConcatArray<string>[]): string[] {
	return ([] as string[]).concat.apply(
		[],
		nodeIds
	);
}

function summarizeHiddenLibraries(node: VisibilityNodeData): string[] {
	const result: string[] = combineIdsInArray(node.children.map((x) => summarizeHiddenLibraries(x)));

	if (!node.isVisible) {
		result.push(node.id);
	}

	return result;
}

const orderToNameMap: Record<SharePointSiteCollectionOrderKind, string> = {
	[SharePointSiteCollectionOrderKind.NameAscending]: getPropertySortText("_Name", "_AToZ"),
	[SharePointSiteCollectionOrderKind.NameDescending]: getPropertySortText("_Name", "_ZToA"),
	[SharePointSiteCollectionOrderKind.CreatedOnDescending]: getPropertySortText("_DateCreated", "_NewToOld"),
	[SharePointSiteCollectionOrderKind.CreatedOnAscending]: getPropertySortText("_DateCreated", "_OldToNew"),
	[SharePointSiteCollectionOrderKind.Custom]: localize("_CustomSorting"),
};

const orderOptions: readonly OptionsMenuItem<SharePointSiteCollectionOrderKind>[] = [
	{
		text: orderToNameMap[SharePointSiteCollectionOrderKind.NameAscending],
		value: SharePointSiteCollectionOrderKind.NameAscending
	},
	{
		text: orderToNameMap[SharePointSiteCollectionOrderKind.NameDescending],
		value: SharePointSiteCollectionOrderKind.NameDescending
	},
	{
		text: orderToNameMap[SharePointSiteCollectionOrderKind.CreatedOnDescending],
		value: SharePointSiteCollectionOrderKind.CreatedOnDescending
	},
	{
		text: orderToNameMap[SharePointSiteCollectionOrderKind.CreatedOnAscending],
		value: SharePointSiteCollectionOrderKind.CreatedOnAscending
	},
	{
		text: orderToNameMap[SharePointSiteCollectionOrderKind.Custom],
		value: SharePointSiteCollectionOrderKind.Custom
	}
];

type FormData = {
	rootNodes: readonly VisibilityNodeData[];
	orderKind: SharePointSiteCollectionOrderKind;
};

type SortingToApply = {
	property: keyof VisibilityNodeData;
	direction: Exclude<DataSourceSortDirection, DataSourceSortDirection.None>;
};

const sortingMap: Record<SharePointSiteCollectionOrderKind, SortingToApply | undefined> = {
	[SharePointSiteCollectionOrderKind.Custom]: undefined,
	[SharePointSiteCollectionOrderKind.NameAscending]: {
		property: "name",
		direction: DataSourceSortDirection.Ascending
	},
	[SharePointSiteCollectionOrderKind.NameDescending]: {
		property: "name",
		direction: DataSourceSortDirection.Descending
	},
	[SharePointSiteCollectionOrderKind.CreatedOnAscending]: {
		property: "createdOn",
		direction: DataSourceSortDirection.Ascending
	},
	[SharePointSiteCollectionOrderKind.CreatedOnDescending]: {
		property: "createdOn",
		direction: DataSourceSortDirection.Descending
	}
};

function applySorting(
	nodes: readonly VisibilityNodeData[],
	orderKind: SharePointSiteCollectionOrderKind
): readonly VisibilityNodeData[] {
	const sortingToApply: SortingToApply | undefined = sortingMap[orderKind];

	if (!sortingToApply) {
		return nodes;
	}

	const { property, direction } = sortingToApply;

	const result: VisibilityNodeData[] = [...nodes];

	result.sort(
		(a, b) => nativeSortFunction(
			a[property],
			b[property],
			direction
		)
	);

	return result;
}
